import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Sun, CloudRain, Snowflake, ArrowRight, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * WeatherBanner — dynamic Paisley weather strip on the homepage.
 *
 * Three condition variants, each with its own hero image + copy + CTA:
 *   - SUNNY  (clear / mainly clear, or partly cloudy ≥ 15°C, or anything ≥ 20°C)
 *   - WET    (drizzle / rain / showers / thunderstorm — WMO 51–67, 80–82, 95–99)
 *   - COLD   (snow / freezing / temp ≤ 5°C — WMO 71–86, 56, 57, 66, 67)
 *
 * Anything else (overcast, fog at mild temps, etc.) shows no banner — we don't
 * want to clutter the homepage with a banner that has nothing to say.
 *
 * Dismissible per browser session.
 */

const SNOW_CODES = new Set([71, 73, 75, 77, 85, 86]);
const WET_CODES = new Set([51, 53, 55, 56, 57, 61, 63, 65, 66, 67, 80, 81, 82, 95, 96, 99]);

const pickVariant = (w) => {
  const code = w.weather_code;
  const temp = w.temp_c;

  // COLD wins first — snow or genuinely cold trumps rain copy.
  if (SNOW_CODES.has(code) || temp <= 5) {
    return {
      kind: 'cold',
      image: '/banner-weather/cold.jpg',
      Icon: Snowflake,
      iconBg: 'bg-sky-100',
      iconColor: 'text-sky-600',
      ring: 'ring-sky-200/70',
      headline: `Crisp ${temp}°C in Paisley`,
      tail: ' — wrap up warm and explore the Abbey, Museum and cosy town-centre spots.',
      ctaLabel: 'Indoor things to do',
      ctaTo: '/attractions',
    };
  }

  // WET — anything from drizzle to thunder.
  if (WET_CODES.has(code)) {
    return {
      kind: 'wet',
      image: '/banner-weather/wet.jpg',
      Icon: CloudRain,
      iconBg: 'bg-slate-200',
      iconColor: 'text-slate-700',
      ring: 'ring-slate-300/70',
      headline: `A wet one in Paisley — ${w.label.toLowerCase()}`,
      tail: ' — duck inside for the Museum, Abbey interior or a riverside café.',
      ctaLabel: 'Find shelter & culture',
      ctaTo: '/attractions',
    };
  }

  // SUNNY — clear (0/1), or partly cloudy (2) and warm enough, or just hot.
  if (code === 0 || code === 1 || (code === 2 && temp >= 15) || temp >= 20) {
    if (temp >= 20) {
      return {
        kind: 'sunny',
        image: '/banner-weather/sunny.jpg',
        Icon: Sun,
        iconBg: 'bg-amber-100',
        iconColor: 'text-amber-500',
        ring: 'ring-amber-200/70',
        headline: `${temp}°C in Paisley — proper sunshine`,
        tail: ' — perfect day for the Abbey gardens or a riverside walk.',
        ctaLabel: 'See things to do',
        ctaTo: '/attractions',
      };
    }
    return {
      kind: 'sunny',
      image: '/banner-weather/sunny.jpg',
      Icon: Sun,
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-500',
      ring: 'ring-amber-200/70',
      headline: `Bright and clear, ${temp}°C in Paisley`,
      tail: ' — a great day to visit Paisley Abbey and explore the town.',
      ctaLabel: 'Plan your visit',
      ctaTo: '/attractions',
    };
  }

  return null;
};

const WeatherBanner = () => {
  const [w, setW] = useState(null);
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem('weather-banner-dismissed') === '1'
  );

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/api/weather`)
      .then((r) => alive && setW(r.data))
      .catch(() => alive && setW(null));
    return () => {
      alive = false;
    };
  }, []);

  const variant = useMemo(() => (w ? pickVariant(w) : null), [w]);

  if (!variant || dismissed) return null;

  const { image, Icon, iconBg, iconColor, ring, headline, tail, ctaLabel, ctaTo, kind } = variant;

  return (
    <section
      className="relative max-w-7xl mx-auto px-6 -mt-6 mb-4"
      data-testid="sunny-day-banner"
      data-variant={kind}
    >
      <div className={`relative overflow-hidden rounded-2xl ring-1 ${ring} bg-slate-100`}>
        {/* Hero image, dimmed slightly so the copy stays readable on any photo */}
        <img
          src={image}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/80 to-white/35" />
        <div className="relative flex flex-wrap items-center justify-between gap-3 px-5 py-4">
          <div className="flex items-center gap-3 min-w-0">
            <span className={`w-9 h-9 rounded-full ${iconBg} flex items-center justify-center shrink-0 shadow-sm`}>
              <Icon size={18} className={iconColor} />
            </span>
            <p className="text-sm font-medium text-slate-900 min-w-0 drop-shadow-sm">
              <span className="font-bold" data-testid="sunny-banner-headline">
                {headline}
              </span>
              {tail}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Link
              to={ctaTo}
              className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold px-4 py-2 rounded-full transition-colors"
              data-testid="sunny-banner-cta"
            >
              {ctaLabel} <ArrowRight size={13} />
            </Link>
            <button
              type="button"
              aria-label="Dismiss weather banner"
              onClick={() => {
                sessionStorage.setItem('weather-banner-dismissed', '1');
                setDismissed(true);
              }}
              className="p-1.5 text-slate-600 hover:text-slate-900 rounded-full hover:bg-white/70 transition-colors"
              data-testid="sunny-banner-dismiss"
            >
              <X size={15} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WeatherBanner;
